import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, ContentChild, input, output, viewChild } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { NavItem } from '@contler/models/ui';
import { LanguageModel } from '@contler/translate/models';
import { TranslateModule } from '@ngx-translate/core';

import { MenuComponent } from '../../menu/menu.component';
import { NavigationComponent } from '../../navigation';

import { FooterComponent } from './footer.component';

@Component({
  selector: 'ctr-layout',
  imports: [
    NavigationComponent,
    MatSidenavContainer,
    MatSidenav,
    MenuComponent,
    TranslateModule,
    MatSidenavContent,
    MatProgressSpinner,
    NgTemplateOutlet,
    CommonModule,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements AfterViewInit {
  // ****************************************************************************
  // * Angular-decorated class properties                                       *
  // ****************************************************************************

  /**
   * Footer component reference.
   */
  @ContentChild(FooterComponent) footer?: FooterComponent;

  private rigthSidenav = viewChild<MatSidenav>('rightSidenav');

  /**
   * The currently selected language.
   */
  actualLanguage = input<LanguageModel | null>();

  /**
   * Indicates whether the process is completed.
   */
  isCompleted = input<boolean>(false);

  /**
   * Indicates whether the footer should be floating.
   */
  isFloatingFooter = input<boolean>(true);

  /**
   * The logo of the hotel.
   */
  hotelLogo = input<string>();

  /**
   * The name of the hotel.
   */
  hotelName = input<string>();

  /**
   * The name of the employer.
   */
  employerName = input<string>();

  /**
   * List of available languages.
   */
  languages = input<LanguageModel[]>([]);

  /**
   * The label for the logout button.
   */
  logOutName = input<string>();

  /**
   * List of navigation items.
   */
  navItems = input<NavItem[]>([]);

  /**
   * The type of layout, can be either 'page' or 'form'.
   */
  type = input<'page' | 'form'>('page');

  /**
   * Width of the right sidenav. Sets the CSS variable --mat-sidenav-container-width.
   */
  sideRightWidth = input<string>();

  /**
   * Event emitted when the language is changed.
   */
  readonly changeLanguage = output<LanguageModel>();

  /**
   * Event emitted when the user logs out.
   */
  readonly logout = output<void>();

  // ****************************************************************************
  // * Getters and setters                                                      *
  // ****************************************************************************

  /**
   * Gets the maximum size for the layout based on its type.
   * @returns The maximum size as a string.
   */
  get maxSize(): string {
    return this.type() === 'page' ? '1280px' : '980px';
  }

  // ****************************************************************************
  // * Lifecycle methods                                                        *
  // ****************************************************************************

  /**
   * After view initialization lifecycle hook.
   */
  ngAfterViewInit(): void {
    if (this.footer) {
      this.footer.setMaxSize(this.type());
    }
  }

  public toggleRightSidenav() {
    const rightSidenav = this.rigthSidenav();
    if (rightSidenav) {
      rightSidenav.toggle();
    }
  }
}
